import {
  TINCTURES,
  isQuartered,
  type AnyArms,
  type Arms,
  type Charge,
  type ChargeKind,
  type Field,
  type Tincture,
} from './blazon';

/**
 * Painting a blazon.
 *
 * One canvas painter, used everywhere arms appear: inside the crest frames in
 * the title panels, and on a kingdom's capital when the world map is pulled
 * right out. Writing it once against canvas rather than twice, once as SVG for
 * the DOM and once as paths for the map, is the whole reason it looks like
 * this. The DOM side simply hands it a canvas element.
 *
 * Charges are drawn in a 100 by 100 box and scaled into place, so a lion is
 * the same lion whether it is forty pixels wide in a list or two hundred and
 * fifty in the crest frame.
 */

/** The outline of a heraldic shield, in the 0..100 box. */
function shieldPath(): Path2D {
  const p = new Path2D();
  p.moveTo(4, 2);
  p.lineTo(96, 2);
  p.lineTo(96, 52);
  // The lower half curves to a point, which is what makes it read as a shield
  // rather than a rectangle with a triangle stuck on it.
  p.bezierCurveTo(96, 78, 76, 93, 50, 99);
  p.bezierCurveTo(24, 93, 4, 78, 4, 52);
  p.closePath();
  return p;
}

// --- charges ----------------------------------------------------------------
//
// Stylised rather than exact. A lion rampant drawn properly is a page of
// bezier and unreadable at forty pixels anyway; these are silhouettes built to
// survive being small, which is the size they are usually seen at.

function lionRampant(p: Path2D): void {
  // Facing dexter, reared, tail curled over the back.
  p.moveTo(30, 92);
  p.lineTo(26, 74);
  p.bezierCurveTo(22, 62, 24, 48, 32, 40);
  p.lineTo(28, 30);
  p.bezierCurveTo(26, 22, 32, 14, 42, 13);
  p.bezierCurveTo(46, 8, 54, 7, 58, 11);
  p.lineTo(66, 8);
  p.lineTo(64, 15);
  p.lineTo(72, 17);
  p.lineTo(63, 21);
  p.bezierCurveTo(66, 27, 63, 33, 56, 34);
  p.lineTo(60, 42);
  p.lineTo(74, 36);
  p.lineTo(72, 44);
  p.lineTo(58, 50);
  p.bezierCurveTo(62, 60, 62, 72, 56, 82);
  p.lineTo(62, 92);
  p.lineTo(50, 92);
  p.lineTo(46, 80);
  p.lineTo(42, 92);
  p.closePath();
  // Tail, up the sinister side and curled.
  p.moveTo(26, 62);
  p.bezierCurveTo(14, 58, 10, 40, 18, 26);
  p.bezierCurveTo(20, 36, 24, 46, 32, 52);
  p.closePath();
}

function lionPassant(p: Path2D): void {
  // Walking, dexter forepaw raised, head to the viewer.
  p.moveTo(14, 74);
  p.lineTo(16, 54);
  p.bezierCurveTo(18, 44, 30, 40, 44, 40);
  p.lineTo(66, 40);
  p.bezierCurveTo(74, 40, 80, 34, 80, 27);
  p.bezierCurveTo(86, 25, 90, 30, 88, 36);
  p.lineTo(94, 38);
  p.lineTo(87, 43);
  p.bezierCurveTo(87, 52, 80, 58, 70, 58);
  p.lineTo(70, 74);
  p.lineTo(62, 74);
  p.lineTo(62, 58);
  p.lineTo(44, 58);
  p.lineTo(44, 74);
  p.lineTo(36, 74);
  p.lineTo(36, 58);
  p.lineTo(28, 58);
  p.lineTo(26, 74);
  p.closePath();
  // Tail over the back.
  p.moveTo(16, 52);
  p.bezierCurveTo(8, 46, 6, 34, 12, 26);
  p.bezierCurveTo(14, 36, 18, 44, 24, 48);
  p.closePath();
}

function eagleBody(p: Path2D, heads: 1 | 2): void {
  // Displayed: wings spread, legs splayed, tail below.
  p.moveTo(50, 30);
  p.bezierCurveTo(36, 26, 20, 26, 8, 34);
  p.bezierCurveTo(16, 38, 22, 44, 26, 52);
  p.bezierCurveTo(18, 52, 12, 58, 10, 66);
  p.bezierCurveTo(22, 62, 32, 62, 40, 66);
  p.lineTo(36, 82);
  p.lineTo(44, 78);
  p.lineTo(46, 90);
  p.lineTo(50, 78);
  p.lineTo(54, 90);
  p.lineTo(56, 78);
  p.lineTo(64, 82);
  p.lineTo(60, 66);
  p.bezierCurveTo(68, 62, 78, 62, 90, 66);
  p.bezierCurveTo(88, 58, 82, 52, 74, 52);
  p.bezierCurveTo(78, 44, 84, 38, 92, 34);
  p.bezierCurveTo(80, 26, 64, 26, 50, 30);
  p.closePath();

  if (heads === 1) {
    p.moveTo(50, 30);
    p.bezierCurveTo(44, 26, 44, 16, 50, 12);
    p.bezierCurveTo(56, 16, 56, 26, 50, 30);
    p.closePath();
    p.moveTo(50, 14);
    p.lineTo(62, 17);
    p.lineTo(50, 21);
    p.closePath();
    return;
  }
  // Two heads, looking outward.
  p.moveTo(44, 30);
  p.bezierCurveTo(36, 26, 36, 16, 43, 12);
  p.bezierCurveTo(49, 16, 49, 26, 44, 30);
  p.closePath();
  p.moveTo(43, 14);
  p.lineTo(31, 17);
  p.lineTo(43, 21);
  p.closePath();
  p.moveTo(56, 30);
  p.bezierCurveTo(64, 26, 64, 16, 57, 12);
  p.bezierCurveTo(51, 16, 51, 26, 56, 30);
  p.closePath();
  p.moveTo(57, 14);
  p.lineTo(69, 17);
  p.lineTo(57, 21);
  p.closePath();
}

function fleur(p: Path2D): void {
  p.moveTo(50, 8);
  p.bezierCurveTo(56, 22, 58, 32, 55, 40);
  p.lineTo(74, 26);
  p.bezierCurveTo(76, 40, 68, 50, 56, 52);
  p.lineTo(56, 60);
  p.lineTo(44, 60);
  p.lineTo(44, 52);
  p.bezierCurveTo(32, 50, 24, 40, 26, 26);
  p.lineTo(45, 40);
  p.bezierCurveTo(42, 32, 44, 22, 50, 8);
  p.closePath();
  // The band across the foot, and the stem below it.
  p.moveTo(34, 62);
  p.lineTo(66, 62);
  p.lineTo(66, 70);
  p.lineTo(34, 70);
  p.closePath();
  p.moveTo(45, 72);
  p.lineTo(55, 72);
  p.bezierCurveTo(58, 82, 56, 88, 50, 92);
  p.bezierCurveTo(44, 88, 42, 82, 45, 72);
  p.closePath();
}

function plainCross(p: Path2D, arm = 16): void {
  p.rect(50 - arm / 2, 4, arm, 92);
  p.rect(6, 46 - arm / 2, 88, arm);
}

function crossPotent(p: Path2D): void {
  const arm = 14;
  p.rect(50 - arm / 2, 8, arm, 84);
  p.rect(10, 46 - arm / 2, 80, arm);
  // The crutch ends that make it potent rather than plain.
  const cap = 6;
  p.rect(50 - arm / 2 - cap, 8, arm + cap * 2, cap);
  p.rect(50 - arm / 2 - cap, 92 - cap, arm + cap * 2, cap);
  p.rect(10, 46 - arm / 2 - cap, cap, arm + cap * 2);
  p.rect(90 - cap, 46 - arm / 2 - cap, cap, arm + cap * 2);
}

function crossPatty(p: Path2D): void {
  // Arms that flare toward the ends, which is what patty means.
  p.moveTo(50, 6);
  p.lineTo(66, 20);
  p.lineTo(58, 38);
  p.lineTo(76, 30);
  p.lineTo(90, 46);
  p.lineTo(76, 62);
  p.lineTo(58, 54);
  p.lineTo(66, 72);
  p.lineTo(50, 88);
  p.lineTo(34, 72);
  p.lineTo(42, 54);
  p.lineTo(24, 62);
  p.lineTo(10, 46);
  p.lineTo(24, 30);
  p.lineTo(42, 38);
  p.lineTo(34, 20);
  p.closePath();
}

function saltire(p: Path2D): void {
  const w = 11;
  p.moveTo(8 - w / 2, 8);
  p.lineTo(8 + w, 4);
  p.lineTo(92, 84);
  p.lineTo(84, 92);
  p.closePath();
  p.moveTo(92, 8 + w / 2);
  p.lineTo(88, 4);
  p.lineTo(6, 84);
  p.lineTo(14, 92);
  p.closePath();
}

function castle(p: Path2D): void {
  // Three towers over a gate, which is Castile's and every Spanish town's.
  p.rect(12, 40, 76, 48);
  // Battlements along the curtain.
  for (let i = 0; i < 5; i++) p.rect(14 + i * 15, 32, 9, 10);
  // The three towers.
  p.rect(14, 18, 18, 24);
  p.rect(41, 12, 18, 30);
  p.rect(68, 18, 18, 24);
  // The gate, punched out by the even-odd fill.
  p.rect(41, 62, 18, 26);
}

function crown(p: Path2D): void {
  p.moveTo(14, 66);
  p.lineTo(86, 66);
  p.lineTo(86, 80);
  p.lineTo(14, 80);
  p.closePath();
  p.moveTo(14, 64);
  p.lineTo(20, 30);
  p.lineTo(34, 50);
  p.lineTo(50, 22);
  p.lineTo(66, 50);
  p.lineTo(80, 30);
  p.lineTo(86, 64);
  p.closePath();
}

function keys(p: Path2D): void {
  // Two keys in saltire, the Church's.
  const one = (flip: number): void => {
    const cx = 50 + flip * 22;
    p.moveTo(cx, 20);
    p.lineTo(cx - flip * 26, 78);
    p.lineTo(cx - flip * 18, 82);
    p.lineTo(cx + flip * 8, 24);
    p.closePath();
    // The bow at the top and the wards at the foot.
    p.moveTo(cx + flip * 2, 10);
    p.bezierCurveTo(cx + flip * 14, 8, cx + flip * 16, 24, cx + flip * 4, 24);
    p.bezierCurveTo(cx - flip * 6, 24, cx - flip * 6, 11, cx + flip * 2, 10);
    p.closePath();
    p.rect(cx - flip * 30, 76, 14, 6);
  };
  one(1);
  one(-1);
}

function escutcheon(p: Path2D): void {
  p.moveTo(16, 12);
  p.lineTo(84, 12);
  p.lineTo(84, 52);
  p.bezierCurveTo(84, 72, 68, 84, 50, 90);
  p.bezierCurveTo(32, 84, 16, 72, 16, 52);
  p.closePath();
}

function crescent(p: Path2D): void {
  p.arc(50, 50, 40, Math.PI * 0.42, Math.PI * 1.58, false);
  p.arc(66, 50, 34, Math.PI * 1.45, Math.PI * 0.55, true);
  p.closePath();
}

function mullet(p: Path2D, points = 6): void {
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? 44 : 18;
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    const x = 50 + Math.cos(a) * r;
    const y = 50 + Math.sin(a) * r;
    if (i === 0) p.moveTo(x, y);
    else p.lineTo(x, y);
  }
  p.closePath();
}

function trident(p: Path2D): void {
  // The Rurikid bident, which is the trident's older cousin.
  p.rect(45, 34, 10, 58);
  p.moveTo(14, 10);
  p.lineTo(24, 10);
  p.lineTo(26, 48);
  p.lineTo(16, 48);
  p.closePath();
  p.moveTo(76, 10);
  p.lineTo(86, 10);
  p.lineTo(84, 48);
  p.lineTo(74, 48);
  p.closePath();
  p.rect(14, 46, 72, 10);
  p.rect(45, 8, 10, 40);
}

function axe(p: Path2D): void {
  // Saint Olaf's, which Norway's lion holds.
  p.rect(46, 16, 8, 76);
  p.moveTo(52, 18);
  p.bezierCurveTo(74, 16, 90, 28, 88, 46);
  p.bezierCurveTo(76, 40, 62, 40, 52, 44);
  p.closePath();
}

/**
 * A harp, for Ireland.
 *
 * Drawn as a frame rather than an instrument: a curved forepillar, a neck
 * arching over, and the deep soundbox down the sinister side. The strings are
 * cut *out* of the fill by the even-odd rule, so the field shows through them
 * and the harp reads as strung at forty pixels, where five separate strokes
 * would just be a smudge.
 */
function harp(p: Path2D): void {
  // Forepillar, down the dexter side.
  p.moveTo(30, 24);
  p.bezierCurveTo(20, 44, 22, 70, 33, 90);
  p.lineTo(45, 90);
  p.bezierCurveTo(34, 70, 32, 46, 40, 30);
  p.closePath();

  // Neck, arching from the head of the pillar across to the soundbox.
  p.moveTo(29, 24);
  p.bezierCurveTo(44, 8, 64, 10, 74, 22);
  p.lineTo(67, 31);
  p.bezierCurveTo(59, 22, 46, 20, 40, 30);
  p.closePath();

  // Soundbox: the heavy curved body the strings run down to.
  p.moveTo(74, 22);
  p.bezierCurveTo(82, 46, 74, 74, 55, 91);
  p.lineTo(33, 91);
  p.lineTo(33, 83);
  p.bezierCurveTo(57, 72, 69, 48, 67, 31);
  p.closePath();

  // Strings, punched through by the even-odd fill.
  for (let i = 1; i <= 5; i++) {
    const t = i / 6;
    const topX = 32 + t * 36;
    const topY = 26 + t * 4;
    const botX = 38 + t * 15;
    const botY = 86 - t * 26;
    const w = 1.4;
    p.moveTo(topX - w, topY);
    p.lineTo(topX + w, topY);
    p.lineTo(botX + w, botY);
    p.lineTo(botX - w, botY);
    p.closePath();
  }
}

function roundel(p: Path2D): void {
  p.arc(50, 50, 40, 0, Math.PI * 2);
}

const CHARGE_PAINTERS: Record<ChargeKind, (p: Path2D) => void> = {
  lionRampant,
  lionPassant,
  eagle: (p) => eagleBody(p, 1),
  eagleDoubled: (p) => eagleBody(p, 2),
  fleur,
  cross: (p) => plainCross(p),
  crossPotent,
  crossPatty,
  saltire,
  castle,
  crown,
  keys,
  escutcheon,
  crescent,
  mullet: (p) => mullet(p),
  trident,
  axe,
  roundel,
  harp,
};

/** Where `n` copies of a charge sit on the shield, as [x, y, size] in the box. */
function arrangement(n: number): [number, number, number][] {
  if (n <= 1) return [[50, 50, 74]];
  if (n === 2) return [[50, 30, 44], [50, 68, 44]];
  if (n === 3) return [[50, 24, 40], [50, 50, 40], [50, 74, 40]];
  // Four in the quarters, clear of a cross through the middle.
  if (n === 4) return [[27, 27, 30], [73, 27, 30], [27, 63, 30], [73, 63, 30]];
  // Five in cross, which is how Portugal wears its escutcheons.
  return [[50, 50, 34], [50, 20, 34], [50, 76, 34], [22, 46, 34], [78, 46, 34]];
}

function drawCharge(ctx: CanvasRenderingContext2D, charge: Charge, box: [number, number, number]): void {
  const [cx, cy, size] = box;
  const scale = (size * (charge.scale ?? 1)) / 100;
  ctx.save();
  ctx.translate(cx - (100 * scale) / 2, cy - (100 * scale) / 2);
  ctx.scale(scale, scale);

  const path = new Path2D();
  CHARGE_PAINTERS[charge.k](path);
  ctx.fillStyle = TINCTURES[charge.a];
  // Even-odd, so a castle's gate and a crescent's bite are holes rather than
  // overpainted solids.
  ctx.fill(path, 'evenodd');
  if (charge.edge) {
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = TINCTURES[charge.edge];
    ctx.stroke(path);
  }
  ctx.restore();
}

function paintField(ctx: CanvasRenderingContext2D, field: Field): void {
  const fill = (t: Tincture, x: number, y: number, w: number, h: number): void => {
    ctx.fillStyle = TINCTURES[t];
    ctx.fillRect(x, y, w, h);
  };

  switch (field.t) {
    case 'plain':
      fill(field.a, 0, 0, 100, 100);
      break;
    case 'perPale':
      fill(field.a, 0, 0, 50, 100);
      fill(field.b, 50, 0, 50, 100);
      break;
    case 'perFess':
      fill(field.a, 0, 0, 100, 50);
      fill(field.b, 0, 50, 100, 50);
      break;
    case 'quarterly':
      fill(field.a, 0, 0, 50, 50);
      fill(field.b, 50, 0, 50, 50);
      fill(field.b, 0, 50, 50, 50);
      fill(field.a, 50, 50, 50, 50);
      break;
    case 'barry': {
      const h = 100 / field.n;
      for (let i = 0; i < field.n; i++) fill(i % 2 ? field.b : field.a, 0, i * h, 100, h + 0.5);
      break;
    }
    case 'paly': {
      const w = 100 / field.n;
      for (let i = 0; i < field.n; i++) fill(i % 2 ? field.b : field.a, i * w, 0, w + 0.5, 100);
      break;
    }
    case 'chequy': {
      const s = 100 / field.n;
      for (let y = 0; y < field.n; y++) {
        for (let x = 0; x < field.n; x++) {
          fill((x + y) % 2 ? field.b : field.a, x * s, y * s, s + 0.5, s + 0.5);
        }
      }
      break;
    }
    case 'seme': {
      fill(field.a, 0, 0, 100, 100);
      // Strewn in offset rows and cut off by the shield's edge, which is what
      // seme means: the field is sown with them, not decorated with a tidy
      // count of them.
      const cols = 4;
      const step = 100 / cols;
      for (let row = 0; row < 5; row++) {
        for (let col = 0; col < cols + 1; col++) {
          const x = col * step + (row % 2 ? step / 2 : 0) - step / 2;
          const y = row * (100 / 5) + 6;
          drawCharge(ctx, { k: field.charge, a: field.on }, [x + step / 2, y + 8, step * 0.78]);
        }
      }
      break;
    }
  }
}

function paintSimple(ctx: CanvasRenderingContext2D, arms: Arms): void {
  paintField(ctx, arms.field);

  if (arms.charges) {
    for (const charge of arms.charges) {
      for (const box of arrangement(charge.n ?? 1)) drawCharge(ctx, charge, box);
    }
  }

  if (arms.bordure) {
    /*
     * A band set in from the edge, not a stroke on it.
     *
     * Stroking the outline put half the line outside the shield, where the
     * clip removed it, and the crest frames overlap the outer few percent of
     * the shield besides. Between the two, the entire visible bordure was
     * exactly the sliver the frame hides: the mark that says "cadet, not the
     * crown" cropped to nothing, and most duchies rendered pixel-identical
     * to their kingdom. Scaling the same outline toward the centre puts the
     * whole band where it can actually be seen.
     */
    ctx.save();
    ctx.translate(50, 50);
    ctx.scale(0.86, 0.86);
    ctx.translate(-50, -50);
    ctx.lineWidth = 12;
    ctx.strokeStyle = TINCTURES[arms.bordure];
    ctx.stroke(shieldPath());
    ctx.restore();
  }

  if (arms.label) {
    // Three pendants hanging from a bar across the chief, dropped clear of
    // the shield's top edge, which the crest frames overlap.
    ctx.fillStyle = TINCTURES[arms.label];
    ctx.fillRect(12, 16, 76, 10);
    for (let i = 0; i < 3; i++) ctx.fillRect(19 + i * 24, 26, 14, 16);
  }
}

/**
 * Paint arms into a canvas.
 *
 * The canvas is cleared, clipped to the shield outline and filled. Quartered
 * arms recurse into four smaller paints, which is how a real quartering
 * works and means it costs nothing to add another.
 */
export function paintArms(
  canvas: HTMLCanvasElement,
  arms: AnyArms,
  width: number,
  height = width * 1.02,
): void {
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  // Scaled on each axis separately, because the openings in the crest frames
  // are taller than they are wide by rather more than a shield is. Stretching
  // the shield a little to fill its frame looks right; leaving it floating in
  // the middle of the opening does not.
  ctx.scale((width * dpr) / 100, (height * dpr) / 100);
  paintInto(ctx, arms);
}

/**
 * A single device on nothing at all, for a title that bore no arms.
 *
 * No shield, no field, no bordure. The crest frame around it supplies the
 * outline, and what sits in the opening is one mark on bare ground.
 *
 * This replaced a painted shield invented per title. That looked like heraldry
 * and was not, and a fabricated coat of arms is a lie told confidently: it
 * invites somebody to believe the Duchy of Zellstein bore azure a lion or,
 * when the Duchy of Zellstein is a name this game made up. A device on the
 * bare opening claims nothing.
 *
 * Drawn with a dark rim, because it has no field behind it and has to read
 * against whatever the frame's opening shows through to.
 */
export function paintSymbol(
  canvas: HTMLCanvasElement,
  kind: ChargeKind,
  tincture: Tincture,
  width: number,
  height = width * 1.02,
): void {
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  symbolInto(ctx, kind, tincture, width * dpr, height * dpr);
}

/** The device, into a context already positioned. Sized in device pixels. */
export function symbolInto(
  ctx: CanvasRenderingContext2D,
  kind: ChargeKind,
  tincture: Tincture,
  width: number,
  height: number,
): void {
  // Fitted to the smaller axis and set a little high: the crest openings taper
  // toward the bottom, so a device centred on the box sits low in the hole.
  const size = Math.min(width, height) * 0.74;
  const scale = size / 100;

  ctx.save();
  ctx.translate((width - size) / 2, height * 0.46 - size / 2);
  ctx.scale(scale, scale);

  const path = new Path2D();
  CHARGE_PAINTERS[kind](path);

  ctx.fillStyle = TINCTURES[tincture];
  ctx.fill(path, 'evenodd');
  ctx.lineJoin = 'round';
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = 'rgba(24, 18, 10, 0.85)';
  ctx.stroke(path);
  ctx.restore();
}

/** The same paint, into a context already positioned and scaled for it. */
export function paintInto(ctx: CanvasRenderingContext2D, arms: AnyArms): void {
  ctx.save();
  ctx.clip(shieldPath());

  if (isQuartered(arms)) {
    const quarters: [number, number][] = [[0, 0], [50, 0], [0, 50], [50, 50]];
    quarters.forEach(([qx, qy], i) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(qx, qy, 50, 50);
      ctx.clip();
      ctx.translate(qx, qy);
      ctx.scale(0.5, 0.5);
      paintSimple(ctx, arms.quarters[i]!);
      ctx.restore();
    });
  } else {
    paintSimple(ctx, arms);
  }

  ctx.restore();

  // A dark line round the edge, which is what stops a pale field dissolving
  // into the gold of the frame it sits inside.
  ctx.save();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = 'rgba(28, 20, 12, 0.55)';
  ctx.stroke(shieldPath());
  ctx.restore();
}

/**
 * Arms rendered once and kept, for the map.
 *
 * The map draws the same handful of crowns on every frame while it is pulled
 * out, and repainting a semé of lilies forty times a second is work nobody
 * asked for.
 */
const rendered = new Map<string, HTMLCanvasElement>();

/** The device cached the same way, for the map's banner layer. */
export function symbolBitmap(
  key: string,
  symbol: { kind: ChargeKind; tincture: Tincture },
  width: number,
  height = width * 1.02,
): HTMLCanvasElement {
  const cacheKey = `sym:${key}@${width}x${Math.round(height)}`;
  let found = rendered.get(cacheKey);
  if (!found) {
    if (rendered.size > 700) rendered.clear();
    found = document.createElement('canvas');
    paintSymbol(found, symbol.kind, symbol.tincture, width, height);
    rendered.set(cacheKey, found);
  }
  return found;
}

export function armsBitmap(
  key: string,
  arms: AnyArms,
  width: number,
  height = width * 1.02,
): HTMLCanvasElement {
  const cacheKey = `${key}@${width}x${Math.round(height)}`;
  let found = rendered.get(cacheKey);
  if (!found) {
    // Room for every duchy on the map at once; at 120 the banner layer
    // repainted arms it had just evicted, every frame.
    if (rendered.size > 700) rendered.clear();
    found = document.createElement('canvas');
    paintArms(found, arms, width, height);
    rendered.set(cacheKey, found);
  }
  return found;
}
